// @flow

import _ from 'lodash';
import moment from 'moment-timezone';
import { store } from 'src/stores/store';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';

/**
 * Convert date object to string
 *
 * @param  {object} date date object
 *
 * @return {string}      date string
 *                       Ex: 2020-02-21T00:00:00.000Z
 */
export const convertDateToString = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  return moment(Date.UTC(year, month, day, 0, 0, 0)).toISOString();
};

/**
 * Convert date string to object
 *
 * @param  {string} stringDate date string
 *                             Ex: 2020-02-21T00:00:00.000Z
 *
 * @return {object}            date object
 */
export const convertStringToDate = (stringDate: string) => {
  const date = new Date(stringDate);
  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();

  return new Date(year, month, day);
};

/**
 * Get date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const getStoreDate = (date: Date | string | number) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  // Format date with language
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDateNumber = DATE_FORMAT[appLanguage].number;
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.tz(dateValue.toISOString(), timezone).format(formatDateNumber);
    return moment(storeDate, formatDateNumber).toDate();
  }
  return dateValue;
};

/**
 * Get time with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const getStoreTime = (date: Date | string | number) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  // Format date with language
  const currentState = store.getState();

  let hour = dateValue.getHours();
  let minute = dateValue.getMinutes();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.tz(dateValue.toISOString(), timezone);

    hour = parseInt(storeDate.format('HH'));
    minute = parseInt(storeDate.format('mm'));
  }
  return { hour, minute };
};

/**
 * Get UTC date from date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const getUTCDate = (
  date: Date,
  hour: number = 0,
  minute: number = 0,
  second: number = 0
) => {
  // Create object date from input
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();
  let dateValue = { year, month, day, hour, minute, second };

  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');

  if (timezone) {
    const formattedDate = moment.parseZone(date).tz(timezone, true).format('YYYY-MM-DD HH:mm:ss');
    return moment(formattedDate);
  }
  return moment(dateValue);
};

export const dateNumberByTimezone = (date): number => {
  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  const momentTz = moment(date).tz(timezone).format('YYYY-MM-DD hh:mm:ss A');
  const dateFormat = new Date(momentTz);
  return Math.floor(dateFormat.getTime() / 1000);
};

/**
 * Get start and end timestamps for a given date and time zone.
 * @param {string} date - The date in DD-MM-YYYY format.
 * @param {string} timeZone - The IANA time zone (e.g., "America/New_York").
 * @returns {object} - An object containing the start and end timestamps, and their formatted strings in local time.
 */
export const getTimestampsByDateAndTimeZone = date => {
  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  // Parse the input date and set the time to 00:00:01 in the given time zone
  const startDate = moment.tz(date, 'DD-MM-YYYY', timezone).startOf('day').add(1, 'second');

  // Parse the input date and set the time to 23:59:59 in the given time zone
  const _date = new Date(date);
  _date.setHours(23, 59, 59);
  const endDate = moment.tz(_date, 'DD-MM-YYYY', timezone).endOf('day');

  // Get timestamps in minute
  const startTimestamp = Math.round(startDate.valueOf() / 1000);
  const endTimestamp = Math.round(endDate.valueOf() / 1000);

  return {
    start: startTimestamp,
    end: endTimestamp
  };
};

/**
 * This function to get end of Date
 * Due to on date picker, the end date is start of date, not end of end date
 * ex: If choose range from date 8 - 0, it return 2024-12-08T00:00:00 - 2024-12-09T00:00:00
 * expected: 2024-12-08T00:00:00 - 2024-12-10T00:00:00
 * @param {*} date
 * @returns
 */
export const getEndOfDate = (date: Date) => {
  const endOfToDate = new Date(date);
  endOfToDate.setDate(date.getDate() + 1);
  return endOfToDate;
};
/**
 * Get UTC date from date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const getUTCNumberDate = (date: Date, type = null) => {
  let _date = new Date(date);
  if (type === 'end') {
    _date?.setHours(23, 59, 59);
  }
  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');

  if (timezone) {
    const formattedDate = moment.parseZone(_date).tz(timezone, true);
    // Get the timestamp (in milliseconds)
    const timestamp = formattedDate.valueOf(); // Number
    return Math.round(timestamp / 1000);
  }
  return Math.round(_date.getTime() / 1000);
};

/**
 * Get time with UTC time
 *
 *  * @return {Date} UTC Date
 */
export const getUTCTime = (time: number) => {
  const dateValue = new Date();
  let hour = Math.floor(time / 60);
  let minute = time % 60;
  // Format date with language
  const currentState = store.getState();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    dateValue.setHours(hour);
    dateValue.setMinutes(minute);

    const storeDate = moment.tz(dateValue, timezone);
    hour = parseInt(storeDate.format('HH'));
    minute = parseInt(storeDate.format('mm'));
  }
  return { hour, minute };
};

/**
 * Show display date range
 *
 * @param  {Date} fromDate the start date
 * @param  {Date} toDate the end date
 *
 *  * @return {Object} save product
 */
export const displayDateRange = (fromDate: Object, toDate: Object) => {
  // Format date with language
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDate = DATE_FORMAT[appLanguage];

  const start = moment.isMoment(fromDate) ? fromDate : moment(fromDate);
  const end = moment.isMoment(toDate) ? toDate : moment(toDate);

  if (moment(end.format(formatDate.number)).isSame(start.format(formatDate.number))) {
    return moment(fromDate).format(formatDate.longDate);
  } else {
    if (start.year() === end.year()) {
      return `${start.format(formatDate.shortDate)} - ${end.format(formatDate.longDate)}`;
    } else {
      return `${start.format(formatDate.longDate)} - ${end.format(formatDate.longDate)}`;
    }
  }
};

export const isValidDate = date => {
  return date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date);
};

/**
 * Display date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const displayDate = (date: Date | string | number, format: string) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  return moment(dateValue).format(format);
};

/**
 * Display date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const displayStoreDate = (date: Date | string | number, format: string) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  const currentState = store.getState();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.tz(dateValue?.toISOString(), timezone).format(format);
    return storeDate;
  }
  return moment(dateValue).format(format);
};

export const displayStoreDateUTC = (date: Date | string | number, format: string) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  const currentState = store.getState();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.utc(dateValue, timezone).format(format);
    return storeDate;
  }
  return moment(dateValue).format(format);
};

export const getUTCISOString = date => {
  const dateFormat = moment(date, DATE_FORMAT.en.fullDateNumber);
  const effected_date = dateFormat.toDate();
  return getUTCDate(
    effected_date,
    effected_date.getHours(),
    effected_date.getMinutes(),
    effected_date.getSeconds()
  ).toISOString();
};

/**
 * Build dates from the date range
 *
 * @param  {string} selectedDateRange the selected range
 *
 *  * @return {Object} save product
 */
export const buildDateFromDateRange = selectedDateRange => {
  // DONE
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDateNumber = DATE_FORMAT[appLanguage].number;

  const today = getStoreDate(new Date());
  today.setHours(23, 59, 59);

  const from = getStoreDate(new Date());
  const to = getStoreDate(new Date());
  from.setHours(0, 0, 1);
  to.setHours(23, 59, 59);

  return parseDateRange(selectedDateRange, from, to, today, formatDateNumber);
};

/**
 * Parse date range by range
 * @param {*} selectedDateRange
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 */
const parseDateRange = (selectedDateRange, from, to, today, formatDateNumber) => {
  const parser = dateRangeParsers[selectedDateRange];
  return parser
    ? parser(from, to, today, formatDateNumber)
    : parseCustomDaysDateRange(from, to, today, formatDateNumber);
};

const dateRangeParsers = {
  today: (from, to, today, formatDateNumber) =>
    parseTodayDateRange(from, to, today, formatDateNumber),
  yesterday: (from, to, today, formatDateNumber) =>
    parseYesterdayDateRange(from, to, formatDateNumber),
  last7Days: (from, to, today, formatDateNumber) =>
    parseLast7DaysDateRange(from, to, formatDateNumber),
  last30Days: (from, to, today, formatDateNumber) =>
    parseLast30DaysDateRange(from, to, formatDateNumber),
  lastMonth: (from, to, today, formatDateNumber) =>
    parseLastMonthDateRange(from, to, formatDateNumber),
  last90Days: (from, to, today, formatDateNumber) =>
    parseLast90DaysDateRange(from, to, formatDateNumber),
  last180Days: (from, to, today, formatDateNumber) =>
    parseLast180DaysDateRange(from, to, formatDateNumber),
  last365Days: (from, to, today, formatDateNumber) =>
    parseLast365DaysDateRange(from, to, formatDateNumber),
};

/**
 * Parse date range for Today
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseTodayDateRange = (from, to, today, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  startDate = moment(from).format(formatDateNumber);
  endDate = moment(to).format(formatDateNumber);

  month = today.getMonth() - 1;
  month = month < 0 ? 11 : month;
  year = today.getFullYear();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: from,
    end: to
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for Yesterday
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseYesterdayDateRange = (from, to, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  const yesterdayFrom = moment(from).add(-1, 'days');
  const yesterdayTo = moment(to).add(-1, 'days');
  startDate = yesterdayFrom.format(formatDateNumber);
  endDate = yesterdayTo.format(formatDateNumber);

  month = yesterdayFrom.month() - 1;
  month = month < 0 ? 11 : month;
  year = yesterdayFrom.year();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: yesterdayFrom.toDate(),
    end: yesterdayTo.toDate()
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for last 7 days
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseLast7DaysDateRange = (from, to, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  const today = moment(to).add(0, 'days');
  const last7Days = moment(from).add(-7, 'days');

  startDate = last7Days.format(formatDateNumber);
  endDate = today.format(formatDateNumber);

  month = today.month() - 1;
  month = month < 0 ? 11 : month;
  year = today.year();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: last7Days.toDate(),
    end: today.toDate()
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for last 30 days
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseLast30DaysDateRange = (from, to, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  const today = moment(to).add(0, 'days');
  const last30Days = moment(from).add(-30, 'days');

  startDate = last30Days.hours(0).minutes(0).seconds(1).format(formatDateNumber);
  endDate = today.format(formatDateNumber);

  month = today.month() - 1;
  month = month < 0 ? 11 : month;
  year = today.year();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: last30Days.toDate(),
    end: today.toDate()
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for last month from 1st to 30th
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseLastMonthDateRange = (from, to, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  const lastMonthStart = moment(from).add(-1, 'months').startOf('month');
  const lastMonthEnd = moment(to).add(-1, 'months').endOf('month');

  startDate = lastMonthStart.format(formatDateNumber);
  endDate = lastMonthEnd.format(formatDateNumber);

  month = lastMonthEnd.month() - 1;
  month = month < 0 ? 11 : month;
  year = lastMonthEnd.year();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: lastMonthStart.toDate(),
    end: lastMonthEnd.toDate()
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for last 90 days
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseLast90DaysDateRange = (from, to, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  const today = moment(to).add(0, 'days');
  const last90Days = moment(from).add(-90, 'days');

  startDate = last90Days.format(formatDateNumber);
  endDate = today.format(formatDateNumber);

  month = today.month() - 1;
  month = month < 0 ? 11 : month;
  year = today.year();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: last90Days.toDate(),
    end: today.toDate()
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for last 180 days
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseLast180DaysDateRange = (from, to, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  const today = moment(to).add(0, 'days');
  const last180Days = moment(from).add(-180, 'days');

  startDate = last180Days.format(formatDateNumber);
  endDate = today.format(formatDateNumber);

  month = today.month() - 1;
  month = month < 0 ? 11 : month;
  year = today.year();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: last180Days.toDate(),
    end: today.toDate()
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for last 365 days
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseLast365DaysDateRange = (from, to, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  const today = moment(to).add(0, 'days');
  const last365Days = moment(from).add(-365, 'days');

  startDate = last365Days.format(formatDateNumber);
  endDate = today.format(formatDateNumber);

  month = today.month() - 1;
  month = month < 0 ? 11 : month;
  year = today.year();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: last365Days.toDate(),
    end: today.toDate()
  };

  return { startDate, endDate, selectedDates, month, year };
};

/**
 * Parse date range for custom days
 * @param {*} from
 * @param {*} to
 * @param {*} today
 * @param {*} formatDateNumber
 * @returns
 */
const parseCustomDaysDateRange = (from, to, today, formatDateNumber) => {
  let startDate, endDate, selectedDates, month, year;
  startDate = moment(from).format(formatDateNumber);
  endDate = moment(to).format(formatDateNumber);

  month = today.getMonth() - 1;
  month = month < 0 ? 11 : month;
  year = today.getFullYear();
  year = month === 11 ? year - 1 : year;

  selectedDates = {
    start: from,
    end: to
  };

  return { startDate, endDate, selectedDates, month, year };
};
